/**
 * AdminGolfScripts — Golf Script Studio (admin-only).
 *
 * Why this exists: every Golf voice track is shipped as a pre-rendered .m4a
 * baked from a hand-crafted phrase list. Editing the script previously meant
 * a code change + running scripts/generate_golf_voice_pack.py. This UI gives
 * the admin a one-screen, no-code path to:
 *   1. Browse the 9 tracks (3 Daily Edge + 6 Deeper Work)
 *   2. Edit the spoken phrases line-by-line
 *   3. Save (persists to `golf_script_overrides`)
 *   4. Regenerate audio (ElevenLabs sleeper-voice → ffmpeg lead-silence
 *      → Whisper resync). Same voice, fully editable.
 *
 * Mirrors AdminAudioResync's vibe — tight, fast, busy-state per row.
 *
 * Route: /admin/golf/scripts
 */
import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { AdminLayout } from "../AdminPages";
import {
  ArrowLeft,
  Pencil,
  Save,
  RotateCw,
  Loader2,
  Plus,
  Trash2,
  Check,
  AlertTriangle,
  X,
  Undo2,
  Square,
  CheckSquare,
  Clock,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` });

const fmtAgo = (iso) => {
  if (!iso) return "never";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return `${Math.round(ms / 1000)}s ago`;
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.round(ms / 3_600_000)}h ago`;
  return `${Math.round(ms / 86_400_000)}d ago`;
};

export default function AdminGolfScripts() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // {track_id, title, phrases:[{text}], original}
  const [busyId, setBusyId] = useState(null);     // track_id currently regenerating
  const [savingId, setSavingId] = useState(null); // track_id currently being saved
  const [errors, setErrors] = useState({});       // track_id → string
  const [flash, setFlash] = useState(null);       // {kind:'ok'|'err', text}

  // ─── Bulk regenerate queue ─────────────────────────────────────
  // Frontend-driven sequential queue: admin checks N tracks, hits "Regenerate
  // selected", we POST /regenerate one at a time so ElevenLabs and Whisper
  // don't get hammered in parallel. queueStatus[track_id] = 'queued' |
  // 'running' | 'done' | 'failed'.
  const [selected, setSelected] = useState(new Set());
  const [queueStatus, setQueueStatus] = useState({});
  const [queueErrors, setQueueErrors] = useState({});
  const [queueRunning, setQueueRunning] = useState(false);
  const queueAbortRef = useRef(false);

  const toggleSelected = (track_id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(track_id)) next.delete(track_id);
      else next.add(track_id);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(scripts.map((s) => s.track_id)));
  const clearSelected = () => setSelected(new Set());

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/golf/scripts`, { headers: auth() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setScripts(d.scripts || []);
    } catch (e) {
      setFlash({ kind: "err", text: `Could not load scripts: ${e.message || e}` });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role === "admin") refresh();
  }, [user]);

  const openEditor = async (track_id) => {
    setErrors((prev) => ({ ...prev, [track_id]: null }));
    try {
      const r = await fetch(`${API}/api/golf/scripts/${track_id}`, { headers: auth() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setEditing({
        track_id,
        title: d.title,
        subtitle: d.subtitle,
        phrases: (d.phrases || []).map((p) => ({ text: p.text || "" })),
        original: JSON.stringify(d.phrases || []),
        has_override: d.has_override,
      });
    } catch (e) {
      setErrors((prev) => ({ ...prev, [track_id]: String(e.message || e) }));
    }
  };

  const closeEditor = () => setEditing(null);

  const updatePhrase = (idx, value) => {
    setEditing((prev) => {
      const phrases = [...prev.phrases];
      phrases[idx] = { text: value };
      return { ...prev, phrases };
    });
  };

  const addPhrase = (idx) => {
    setEditing((prev) => {
      const phrases = [...prev.phrases];
      phrases.splice(idx + 1, 0, { text: "" });
      return { ...prev, phrases };
    });
  };

  const removePhrase = (idx) => {
    setEditing((prev) => {
      const phrases = prev.phrases.filter((_, i) => i !== idx);
      return { ...prev, phrases: phrases.length ? phrases : [{ text: "" }] };
    });
  };

  const movePhrase = (idx, dir) => {
    setEditing((prev) => {
      const phrases = [...prev.phrases];
      const target = idx + dir;
      if (target < 0 || target >= phrases.length) return prev;
      [phrases[idx], phrases[target]] = [phrases[target], phrases[idx]];
      return { ...prev, phrases };
    });
  };

  const saveScript = async (then = null) => {
    if (!editing) return;
    setSavingId(editing.track_id);
    setErrors((prev) => ({ ...prev, [editing.track_id]: null }));
    try {
      const phrases = editing.phrases.filter((p) => p.text && p.text.trim());
      if (!phrases.length) throw new Error("At least one phrase is required.");
      const r = await fetch(`${API}/api/golf/scripts/${editing.track_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...auth() },
        body: JSON.stringify({ phrases: phrases.map((p) => ({ text: p.text.trim() })) }),
      });
      if (!r.ok) {
        const t = await r.text();
        throw new Error(`HTTP ${r.status} · ${t}`);
      }
      setFlash({ kind: "ok", text: `Script saved (${phrases.length} phrases)` });
      await refresh();
      if (then === "regenerate") {
        await regenerate(editing.track_id, /* keepEditor */ true);
      } else {
        closeEditor();
      }
    } catch (e) {
      setErrors((prev) => ({ ...prev, [editing.track_id]: String(e.message || e) }));
    } finally {
      setSavingId(null);
    }
  };

  const revertToCatalogue = async () => {
    if (!editing) return;
    if (!editing.has_override) return;
    if (!window.confirm("Revert this track to the original catalogue script? Your custom edits will be lost (audio is unchanged until you regenerate).")) return;
    try {
      const r = await fetch(`${API}/api/golf/scripts/${editing.track_id}`, {
        method: "DELETE",
        headers: auth(),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setFlash({ kind: "ok", text: "Reverted to catalogue script" });
      await refresh();
      await openEditor(editing.track_id);
    } catch (e) {
      setErrors((prev) => ({ ...prev, [editing.track_id]: String(e.message || e) }));
    }
  };

  const regenerate = async (track_id, keepEditor = false) => {
    if (busyId) return;
    if (!window.confirm(
      "Regenerate this track's voice via ElevenLabs?\n\n" +
      "This calls ElevenLabs (~30–60s) and Whisper resync. Same voice, new script. " +
      "All listeners will hear the new audio on next play."
    )) return;
    setBusyId(track_id);
    setErrors((prev) => ({ ...prev, [track_id]: null }));
    try {
      const r = await fetch(`${API}/api/golf/regenerate/${track_id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...auth() },
        body: JSON.stringify({}),
      });
      if (!r.ok) {
        const t = await r.text();
        throw new Error(`HTTP ${r.status} · ${t}`);
      }
      const d = await r.json();
      const dur = d.duration ? `${d.duration}s` : "audio refreshed";
      setFlash({ kind: "ok", text: `Regenerated · ${dur}` });
      await refresh();
      if (!keepEditor) closeEditor();
    } catch (e) {
      setErrors((prev) => ({ ...prev, [track_id]: String(e.message || e) }));
    } finally {
      setBusyId(null);
    }
  };

  // ─── Bulk queue runner — sequential, one ElevenLabs call at a time ──
  const runQueue = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    if (queueRunning) return;
    if (!window.confirm(
      `Regenerate ${ids.length} track${ids.length > 1 ? "s" : ""} sequentially?\n\n` +
      `Each call hits ElevenLabs + Whisper (~30–60s each → roughly ${Math.round(ids.length * 0.75)} min total).\n` +
      `Listeners will hear the new audio on next play. Keep this tab open.`
    )) return;

    queueAbortRef.current = false;
    setQueueRunning(true);
    setQueueErrors({});
    // Mark every selected track as queued upfront
    setQueueStatus(() => Object.fromEntries(ids.map((id) => [id, "queued"])));

    let okCount = 0;
    let failCount = 0;
    for (const id of ids) {
      if (queueAbortRef.current) {
        setQueueStatus((p) => ({ ...p, [id]: "queued" })); // leave as queued (skipped)
        continue;
      }
      setQueueStatus((p) => ({ ...p, [id]: "running" }));
      try {
        const r = await fetch(`${API}/api/golf/regenerate/${id}`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...auth() },
          body: JSON.stringify({}),
        });
        if (!r.ok) {
          const t = await r.text();
          throw new Error(`HTTP ${r.status} · ${t.slice(0, 200)}`);
        }
        await r.json();
        setQueueStatus((p) => ({ ...p, [id]: "done" }));
        okCount += 1;
      } catch (e) {
        setQueueErrors((p) => ({ ...p, [id]: String(e.message || e) }));
        setQueueStatus((p) => ({ ...p, [id]: "failed" }));
        failCount += 1;
      }
    }

    setQueueRunning(false);
    queueAbortRef.current = false;
    await refresh();
    if (queueAbortRef.current === false) {
      setFlash({
        kind: failCount === 0 ? "ok" : "err",
        text: `Bulk regen complete — ${okCount} done${failCount ? `, ${failCount} failed` : ""}.`,
      });
    }
  };

  const cancelQueue = () => {
    queueAbortRef.current = true;
    setFlash({ kind: "err", text: "Cancel requested — finishing current track, then stopping." });
  };

  const clearQueueResults = () => {
    setQueueStatus({});
    setQueueErrors({});
    clearSelected();
  };

  if (user?.role !== "admin") {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <AlertTriangle size={32} className="mx-auto text-amber-500 mb-3" />
          <p className="text-[#78716C]">Admin access required.</p>
        </div>
      </AdminLayout>
    );
  }

  const dailyEdgeIds = new Set(["pre_tee_primer", "poor_shot_reset", "flow_state_reset"]);
  const dailyEdge = scripts.filter((s) => dailyEdgeIds.has(s.track_id));
  const deeperWork = scripts.filter((s) => !dailyEdgeIds.has(s.track_id));

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-golf-scripts">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-heading text-xl text-[#292524]">Golf Script Studio</h2>
            <p className="text-sm text-[#78716C]">
              Edit the spoken copy for any of the 9 golf voice tracks. Save to persist,
              regenerate to push new audio live (same sleeper voice).
            </p>
          </div>
          <button
            onClick={() => navigate("/admin")}
            className="flex items-center gap-2 text-sm text-[#78716C] hover:text-[#292524]"
            data-testid="golf-scripts-back-btn"
          >
            <ArrowLeft size={16} /> Back
          </button>
        </div>

        {flash && (
          <div
            className={`rounded-xl px-4 py-3 text-sm flex items-center justify-between ${
              flash.kind === "ok"
                ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
                : "bg-red-50 text-red-800 border border-red-200"
            }`}
            data-testid="golf-scripts-flash"
          >
            <div className="flex items-center gap-2">
              {flash.kind === "ok" ? <Check size={14} /> : <AlertTriangle size={14} />}
              <span>{flash.text}</span>
            </div>
            <button onClick={() => setFlash(null)} className="opacity-60 hover:opacity-100">
              <X size={14} />
            </button>
          </div>
        )}

        {loading ? (
          <div className="text-center py-12 text-[#78716C]" data-testid="golf-scripts-loading">
            <Loader2 size={20} className="inline animate-spin mr-2" /> Loading…
          </div>
        ) : (
          <>
            {/* Bulk select toolbar */}
            <div
              className="flex items-center justify-between rounded-xl bg-[#F5F5F4] border border-[#E6E2DD] px-4 py-2.5 text-xs text-[#78716C]"
              data-testid="golf-scripts-bulk-toolbar"
            >
              <div className="flex items-center gap-3">
                <button
                  onClick={selectAll}
                  disabled={queueRunning}
                  className="hover:text-[#292524] disabled:opacity-40"
                  data-testid="golf-scripts-select-all"
                >
                  Select all
                </button>
                <span className="text-[#D6D3D1]">·</span>
                <button
                  onClick={clearSelected}
                  disabled={queueRunning || selected.size === 0}
                  className="hover:text-[#292524] disabled:opacity-40"
                  data-testid="golf-scripts-clear-selected"
                >
                  Clear
                </button>
                {Object.keys(queueStatus).length > 0 && !queueRunning && (
                  <>
                    <span className="text-[#D6D3D1]">·</span>
                    <button
                      onClick={clearQueueResults}
                      className="hover:text-[#292524]"
                      data-testid="golf-scripts-clear-queue-results"
                    >
                      Clear queue results
                    </button>
                  </>
                )}
              </div>
              <span data-testid="golf-scripts-selected-count">
                {selected.size > 0 ? `${selected.size} selected` : "Tap rows to multi-select"}
              </span>
            </div>

            <ScriptGroup
              label="Daily Edge"
              rows={dailyEdge}
              {...{ openEditor, regenerate, busyId, errors, selected, toggleSelected, queueStatus, queueErrors, queueRunning }}
            />
            <ScriptGroup
              label="Deeper Work"
              rows={deeperWork}
              {...{ openEditor, regenerate, busyId, errors, selected, toggleSelected, queueStatus, queueErrors, queueRunning }}
            />
          </>
        )}
      </div>

      {/* Sticky bulk-regen action bar */}
      {(selected.size > 0 || queueRunning) && (
        <BulkActionBar
          count={selected.size}
          queueStatus={queueStatus}
          queueRunning={queueRunning}
          onRun={runQueue}
          onCancel={cancelQueue}
        />
      )}

      {editing && (
        <ScriptEditor
          editing={editing}
          onClose={closeEditor}
          onPhraseChange={updatePhrase}
          onAddPhrase={addPhrase}
          onRemovePhrase={removePhrase}
          onMovePhrase={movePhrase}
          onSave={() => saveScript()}
          onSaveAndRegenerate={() => saveScript("regenerate")}
          onRevert={revertToCatalogue}
          saving={savingId === editing.track_id}
          regenerating={busyId === editing.track_id}
          error={errors[editing.track_id]}
        />
      )}
    </AdminLayout>
  );
}

const ScriptGroup = ({
  label, rows, openEditor, regenerate, busyId, errors,
  selected, toggleSelected, queueStatus, queueErrors, queueRunning,
}) => (
  <div className="space-y-2" data-testid={`golf-scripts-group-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <h3 className="text-xs uppercase tracking-widest text-[#A8A29E]">{label}</h3>
    <div className="space-y-2">
      {rows.map((r) => {
        const isSelected = selected?.has(r.track_id);
        const qStatus = queueStatus?.[r.track_id];
        const qErr = queueErrors?.[r.track_id];
        return (
          <div
            key={r.track_id}
            className={`rounded-xl border bg-white px-4 py-3 flex items-center gap-3 transition-colors ${
              isSelected ? "border-emerald-400 ring-1 ring-emerald-200" : "border-[#E6E2DD]"
            }`}
            data-testid={`golf-script-row-${r.track_id}`}
          >
            <button
              onClick={() => !queueRunning && toggleSelected?.(r.track_id)}
              disabled={queueRunning}
              className="text-emerald-700 hover:text-emerald-800 disabled:opacity-40"
              title={isSelected ? "Deselect" : "Select for bulk regen"}
              data-testid={`golf-script-select-${r.track_id}`}
            >
              {isSelected ? <CheckSquare size={18} /> : <Square size={18} className="text-[#D6D3D1]" />}
            </button>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h4 className="font-medium text-[#292524] truncate">{r.title}</h4>
                {r.has_override && (
                  <span className="text-[10px] uppercase tracking-wider text-amber-700 bg-amber-100 rounded px-1.5 py-0.5">
                    Edited
                  </span>
                )}
                {qStatus && <QueueBadge status={qStatus} />}
              </div>
              <p className="text-xs text-[#78716C] truncate">{r.subtitle}</p>
              <p className="text-xs text-[#A8A29E] mt-1">
                {r.phrase_count} phrases · {r.duration}s ·{" "}
                {r.has_override ? `last edit ${fmtAgo(r.updated_at)}` : "catalogue copy"}
              </p>
              {(errors[r.track_id] || qErr) && (
                <p className="text-xs text-red-600 mt-1" data-testid={`golf-script-error-${r.track_id}`}>
                  {qErr || errors[r.track_id]}
                </p>
              )}
            </div>
            <button
              onClick={() => openEditor(r.track_id)}
              disabled={busyId === r.track_id || queueRunning}
              className="flex items-center gap-1.5 text-xs text-[#292524] hover:text-emerald-700 px-3 py-1.5 rounded-lg border border-[#E6E2DD] hover:bg-emerald-50 transition-colors disabled:opacity-40"
              data-testid={`golf-script-edit-${r.track_id}`}
            >
              <Pencil size={14} /> Edit
            </button>
            <button
              onClick={() => regenerate(r.track_id)}
              disabled={busyId === r.track_id || queueRunning}
              className="flex items-center gap-1.5 text-xs text-white bg-emerald-700 hover:bg-emerald-800 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-40 min-w-[120px] justify-center"
              data-testid={`golf-script-regenerate-${r.track_id}`}
            >
              {busyId === r.track_id ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Generating…
                </>
              ) : (
                <>
                  <RotateCw size={14} /> Regenerate
                </>
              )}
            </button>
          </div>
        );
      })}
    </div>
  </div>
);

const QueueBadge = ({ status }) => {
  const map = {
    queued:     { txt: "Queued",      cls: "bg-stone-100 text-stone-600",     Icon: Clock },
    running:    { txt: "Generating…", cls: "bg-emerald-100 text-emerald-700", Icon: Loader2, spin: true },
    done:       { txt: "Done",        cls: "bg-emerald-600 text-white",       Icon: Check },
    failed:     { txt: "Failed",      cls: "bg-red-100 text-red-700",         Icon: AlertTriangle },
  };
  const m = map[status];
  if (!m) return null;
  const Icon = m.Icon;
  return (
    <span
      className={`text-[10px] uppercase tracking-wider rounded px-1.5 py-0.5 inline-flex items-center gap-1 ${m.cls}`}
      data-testid={`golf-script-queue-status-${status}`}
    >
      <Icon size={10} className={m.spin ? "animate-spin" : ""} /> {m.txt}
    </span>
  );
};

const BulkActionBar = ({ count, queueStatus, queueRunning, onRun, onCancel }) => {
  const all = Object.values(queueStatus || {});
  const done = all.filter((s) => s === "done").length;
  const failed = all.filter((s) => s === "failed").length;
  const running = all.filter((s) => s === "running").length;
  const total = all.length || count;

  return (
    <div
      className="fixed left-0 right-0 bottom-20 z-50 mx-3 rounded-2xl px-4 py-3 bg-white/95 backdrop-blur-md border border-[#E6E2DD] shadow-2xl flex items-center justify-between gap-3"
      data-testid="golf-scripts-bulk-bar"
    >
      <div className="text-sm text-[#292524]">
        {queueRunning ? (
          <span data-testid="golf-scripts-bulk-progress">
            <Loader2 size={14} className="inline animate-spin mr-1" />
            Regenerating {done + running} of {total}
            {failed > 0 && <span className="text-red-600"> · {failed} failed</span>}
          </span>
        ) : all.length > 0 ? (
          <span>
            <Check size={14} className="inline mr-1 text-emerald-600" />
            Bulk regen complete — {done} done{failed ? `, ${failed} failed` : ""}.
          </span>
        ) : (
          <span>{count} track{count !== 1 ? "s" : ""} selected for bulk regenerate</span>
        )}
        <p className="text-xs text-[#A8A29E] mt-0.5">
          {queueRunning ? "Keep this tab open until complete." : `~${Math.round(count * 0.75)} min total at ~45s per track.`}
        </p>
      </div>
      {queueRunning ? (
        <button
          onClick={onCancel}
          className="flex items-center gap-1.5 text-sm px-4 py-2 rounded-lg border border-red-200 text-red-700 hover:bg-red-50"
          data-testid="golf-scripts-bulk-cancel"
        >
          <X size={14} /> Cancel after current
        </button>
      ) : (
        <button
          onClick={onRun}
          disabled={count === 0}
          className="flex items-center gap-1.5 text-sm px-4 py-2 rounded-lg bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-40"
          data-testid="golf-scripts-bulk-run"
        >
          <RotateCw size={14} /> Regenerate {count > 0 ? `${count} ` : ""}selected
        </button>
      )}
    </div>
  );
};

const ScriptEditor = ({
  editing,
  onClose,
  onPhraseChange,
  onAddPhrase,
  onRemovePhrase,
  onMovePhrase,
  onSave,
  onSaveAndRegenerate,
  onRevert,
  saving,
  regenerating,
  error,
}) => (
  <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40" data-testid="golf-script-editor">
    <div
      className="bg-white w-full sm:max-w-2xl sm:rounded-2xl rounded-t-2xl shadow-2xl max-h-[92vh] flex flex-col"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="px-5 py-4 border-b border-[#E6E2DD] flex items-center justify-between">
        <div className="min-w-0">
          <h3 className="font-heading text-lg text-[#292524] truncate">{editing.title}</h3>
          <p className="text-xs text-[#78716C] truncate">{editing.subtitle}</p>
        </div>
        <button onClick={onClose} className="p-1 text-[#A8A29E] hover:text-[#292524]" data-testid="golf-script-editor-close">
          <X size={18} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
        {editing.phrases.map((p, idx) => (
          <div key={idx} className="flex gap-2 items-start" data-testid={`golf-script-phrase-${idx}`}>
            <div className="text-xs text-[#A8A29E] w-5 pt-2 text-right">{idx + 1}.</div>
            <textarea
              value={p.text}
              onChange={(e) => onPhraseChange(idx, e.target.value)}
              className="flex-1 min-h-[44px] resize-y rounded-lg border border-[#E6E2DD] focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 px-3 py-2 text-sm text-[#292524] outline-none"
              placeholder="Type a spoken line…"
              data-testid={`golf-script-phrase-input-${idx}`}
            />
            <div className="flex flex-col gap-1">
              <button
                onClick={() => onMovePhrase(idx, -1)}
                disabled={idx === 0}
                className="text-[10px] text-[#78716C] hover:text-[#292524] disabled:opacity-30 px-1"
                title="Move up"
                data-testid={`golf-script-phrase-up-${idx}`}
              >
                ↑
              </button>
              <button
                onClick={() => onMovePhrase(idx, 1)}
                disabled={idx === editing.phrases.length - 1}
                className="text-[10px] text-[#78716C] hover:text-[#292524] disabled:opacity-30 px-1"
                title="Move down"
                data-testid={`golf-script-phrase-down-${idx}`}
              >
                ↓
              </button>
            </div>
            <button
              onClick={() => onAddPhrase(idx)}
              className="p-1.5 text-[#78716C] hover:text-emerald-700"
              title="Add phrase below"
              data-testid={`golf-script-phrase-add-${idx}`}
            >
              <Plus size={14} />
            </button>
            <button
              onClick={() => onRemovePhrase(idx)}
              className="p-1.5 text-[#78716C] hover:text-red-600"
              title="Remove phrase"
              data-testid={`golf-script-phrase-remove-${idx}`}
            >
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>

      {error && (
        <div className="px-5 py-2 text-xs text-red-600 border-t border-red-100 bg-red-50" data-testid="golf-script-editor-error">
          {error}
        </div>
      )}

      <div className="px-5 py-4 border-t border-[#E6E2DD] flex flex-wrap gap-2 justify-between items-center">
        <button
          onClick={onRevert}
          disabled={!editing.has_override || saving || regenerating}
          className="flex items-center gap-1.5 text-xs text-[#78716C] hover:text-amber-700 disabled:opacity-30"
          data-testid="golf-script-revert-btn"
        >
          <Undo2 size={14} /> Revert to catalogue
        </button>
        <div className="flex gap-2">
          <button
            onClick={onSave}
            disabled={saving || regenerating}
            className="flex items-center gap-1.5 text-sm px-4 py-2 rounded-lg border border-[#E6E2DD] text-[#292524] hover:bg-[#F5F5F4] disabled:opacity-50"
            data-testid="golf-script-save-btn"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Save
          </button>
          <button
            onClick={onSaveAndRegenerate}
            disabled={saving || regenerating}
            className="flex items-center gap-1.5 text-sm px-4 py-2 rounded-lg bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-50 min-w-[180px] justify-center"
            data-testid="golf-script-save-regenerate-btn"
          >
            {regenerating ? (
              <>
                <Loader2 size={14} className="animate-spin" /> Generating audio…
              </>
            ) : (
              <>
                <RotateCw size={14} /> Save &amp; Regenerate
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  </div>
);
